import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, MapPin, CreditCard } from 'lucide-react';
import { Button } from '@caruso/ui';
import { storeService } from '@/services/store.service';
import { StoreLayout } from '@/components/layouts/store-layout';

export function OrderConfirmationPage() {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const navigate = useNavigate();

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', orderNumber],
    queryFn: () => storeService.getOrderByNumber(orderNumber!),
    enabled: !!orderNumber,
  });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <StoreLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-brand-ivory/50">Carregando pedido...</div>
        </div>
      </StoreLayout>
    );
  }

  if (!order) {
    return (
      <StoreLayout>
        <div className="text-center py-12">
          <Package className="w-16 h-16 text-brand-gold/40 mx-auto mb-4" />
          <p className="text-brand-ivory/50 mb-4">Pedido não encontrado</p>
          <Button variant="outline" onClick={() => navigate('/loja')}>
            Voltar para a loja
          </Button>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Success Message */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-6">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-serif text-brand-gold mb-2">Pedido Confirmado!</h1>
          <p className="text-brand-ivory/60">
            Número do pedido: <span className="text-brand-gold font-semibold">#{order.orderNumber}</span>
          </p>
          <p className="text-brand-ivory/60 text-sm mt-2">
            Recebido em {formatDate(order.createdAt)}
          </p>
        </motion.div>

        <div className="space-y-6">
          {/* Order Items */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6"
          >
            <h2 className="text-xl font-serif text-brand-gold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Itens do Pedido
            </h2>
            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.id} className="flex justify-between items-center border-b border-brand-gold/10 pb-4 last:border-0">
                  <div className="flex items-center gap-4">
                    {item.product.images[0] && (
                      <div className="w-16 h-16 bg-brand-ink rounded overflow-hidden">
                        <img
                          src={item.product.images[0].url}
                          alt={item.productName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div>
                      <p className="text-brand-ivory font-semibold">{item.productName}</p>
                      <p className="text-brand-ivory/60 text-sm">Quantidade: {item.quantity}</p>
                    </div>
                  </div>
                  <p className="text-brand-gold font-semibold">{formatPrice(item.subtotal)}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Delivery Address */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6"
          >
            <h2 className="text-xl font-serif text-brand-gold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Endereço de Entrega
            </h2>
            <div className="text-brand-ivory/70 space-y-1">
              <p className="font-semibold text-brand-ivory">{order.customerName}</p>
              <p>{order.address}, {order.number}</p>
              {order.complement && <p>{order.complement}</p>}
              <p>{order.neighborhood}</p>
              <p>{order.city} - {order.state}</p>
              <p>CEP: {order.zipCode}</p>
            </div>
          </motion.div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-brand-ink/50 border border-brand-gold/20 rounded-lg p-6"
          >
            <h2 className="text-xl font-serif text-brand-gold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Resumo do Pagamento
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between text-brand-ivory/70">
                <span>Subtotal</span>
                <span>{formatPrice(order.subtotal)}</span>
              </div>
              <div className="flex justify-between text-brand-ivory/70">
                <span>Frete</span>
                <span>{formatPrice(order.shipping)}</span>
              </div>
              <div className="border-t border-brand-gold/20 pt-3">
                <div className="flex justify-between text-brand-gold text-xl font-bold">
                  <span>Total</span>
                  <span>{formatPrice(order.total)}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex gap-4 justify-center"
        >
          <Button onClick={() => navigate('/loja')}>
            Voltar para a Loja
          </Button>
        </motion.div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center text-brand-ivory/50 text-sm"
        >
          <p>Enviamos um e-mail de confirmação para {order.customerEmail}</p>
          <p className="mt-2">Em caso de dúvidas, entre em contato conosco.</p>
        </motion.div>
      </div>
    </StoreLayout>
  );
}
